import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { AuthService } from '../../../core/auth/auth.service';
import { LocalStorageHelper } from '../../../core/helpers/local-storage.helper';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, ToastModule],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss'
})
export class ResetPasswordComponent implements OnInit {
  resetPasswordForm!: FormGroup;
  isBtnLoading: boolean = false;
  resetToken: string | null = null;
  showNewPassword: boolean = false;
  showConfirmPassword: boolean = false;

  constructor(
    private _formBuilder: FormBuilder,
    private _router: Router,
    private _authService: AuthService,
    private messageService: MessageService,
    private localStorage: LocalStorageHelper
  ) {
    this.resetPasswordForm = this._formBuilder.group({
      newPassword: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator });
  }

  passwordMatchValidator: ValidatorFn = (control: AbstractControl): { [key: string]: boolean } | null => {
    const newPassword = control.get('newPassword');
    const confirmPassword = control.get('confirmPassword');
    if (newPassword && confirmPassword && newPassword.value !== confirmPassword.value) {
      return { 'passwordMismatch': true };
    }
    return null;
  };

  ngOnInit(): void {
    this.resetToken = this.localStorage.getItem('resetToken');
    if (!this.resetToken) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Session expired. Please request OTP again.'
      });
      this._router.navigate(['/forget-password']);
      return;
    }
  }

  submitResetPassword(): void {
    if (this.resetPasswordForm.invalid) {
      Object.keys(this.resetPasswordForm.controls).forEach((key: any) => {
        this.resetPasswordForm.controls[key].markAsTouched();
        this.resetPasswordForm.controls[key].markAsDirty();
      });
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please enter valid data'
      });
      return;
    }

    const { newPassword } = this.resetPasswordForm.value;

    this.isBtnLoading = true;
    this._authService.resetPassword({
      reset_token: this.resetToken!,
      new_password: newPassword
    }).subscribe({
      next: (result: any) => {
        if (result && result.success) {
          this.localStorage.removeItem('resetToken');
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: result.message || 'Password updated successfully! Please log in.'
          });
          setTimeout(() => {
            this._router.navigate(['/login']);
          }, 1500);
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: result?.message || result?.Message || 'Failed to reset password'
          });
          this.isBtnLoading = false;
        }
      },
      error: (error: any) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.error?.message || error.error?.Message || 'Failed to reset password'
        });
        this.isBtnLoading = false;
      }
    });
  }
}
